import { PageTitle, TextInput } from "src/components";
import styles from "src/pages/Checkout/styles.module.scss";
import { CheckoutOrderDetails } from "src/pages/Checkout/CheckoutOrderDetails/CheckoutOrderDetails";
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { getEnvVariable } from "src/config/getConfig";
import { useSelector } from "react-redux";
import { State } from "src/state/state";
import { useScrollToTop, useDesign } from "src/common/hooks";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMenuPath } from "src/Router/routes";
import { animateScroll as scroll } from "react-scroll";
import { isValidEmailAddress, isValidPhoneNumber } from "src/common/validation";
import palette from "src/common/styles/palette.module.scss";
import { PromotionCheckbox } from "src/pages/Checkout/PromotionCheckbox/PromotionCheckbox";
import { logUpdatedPromotionsConsentAtCheckoutShownToAnalytics } from "src/common/analytics";
import { UIVariants } from "src/state/globalUIVariant/types";

export const Checkout = () => {
  useScrollToTop();
  const design = useDesign();

  const customer = useSelector(
    (state: State) => state.customers.currentCustomer,
  );
  const restaurant = useSelector(
    (state: State) => state.restaurants.currentRestaurant,
  );
  const promotionalCheckboxUIVariant = useSelector(
    (state: State) =>
      state.globalUIVariant.promotionalCheckboxVariant as UIVariants,
  );

  const stripePromise = loadStripe(getEnvVariable("STRIPE_PUBLISHABLE_KEY"), {
    stripeAccount: restaurant?.stripeConnectedAccountId || undefined,
  });

  const checkoutSession = useSelector((state: State) => state.checkoutSession);
  const navigate = useNavigate();

  useEffect(() => {
    if (!checkoutSession.order) {
      navigate(getMenuPath());
    }

    logUpdatedPromotionsConsentAtCheckoutShownToAnalytics(
      promotionalCheckboxUIVariant,
    );
  }, [checkoutSession]);

  const [nameForTheOrder, setNameForTheOrder] = useState("");
  const [phoneForTheOrder, setPhoneForTheOrder] = useState("");
  const [emailForTheOrder, setEmailForTheOrder] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  const [phoneError, setPhoneError] = useState<string | undefined>();
  const [emailError, setEmailError] = useState<string | undefined>();
  const [isConsentingToPromotions, setIsConsentingToPromotions] =
    useState(true);

  const areFieldsValid = useCallback(() => {
    let isValid = true;

    if (!customer && nameForTheOrder.trim() === "") {
      setNameError("Please enter a name for this order.");
      scroll.scrollToTop();
      isValid = false;
    }

    if (!customer && phoneForTheOrder.trim() === "") {
      setPhoneError("Please enter a phone number for this order.");
      scroll.scrollToTop();
      isValid = false;
    }

    if (!customer && !isValidPhoneNumber(phoneForTheOrder)) {
      setPhoneError("Please enter a valid phone number.");
      scroll.scrollToTop();
      isValid = false;
    }

    if (
      !customer &&
      (emailForTheOrder.trim() === "" ||
        !isValidEmailAddress(emailForTheOrder.trim()))
    ) {
      setEmailError("Please enter a valid email address.");
      scroll.scrollToTop();
      isValid = false;
    }

    return isValid;
  }, [customer, nameForTheOrder, phoneForTheOrder, emailForTheOrder]);

  if (!checkoutSession.order) {
    return <div />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret: checkoutSession.paymentIntentClientSecret,
        appearance: {
          theme: "stripe" as "stripe" | "night",
          variables: {
            colorPrimary: design.buttonColor,
            colorBackground: design.cardColor,
            colorText: design.textColor,
            colorDanger: palette.red,
            spacingUnit: "4px",
            borderRadius: "4px",
          },
        },
      }}
    >
      <div className={styles.Checkout} data-testid="checkout-container">
        <div className={styles.checkoutContainer}>
          <div className={styles.checkoutPaymentContainer}>
            <PageTitle title="Checkout" className={styles.title} />
            {!customer && (
              <>
                <TextInput
                  className={styles.textInput}
                  testId="name-for-order-input"
                  label="Name for the Order"
                  autoComplete="given-name"
                  value={nameForTheOrder}
                  onChangeText={(newText) => {
                    setNameError(undefined);
                    setNameForTheOrder(newText);
                  }}
                  placeholder="Enter a name for this order..."
                  errorMessage={nameError}
                />
                <TextInput
                  className={styles.textInput}
                  testId="phone-for-order-input"
                  label="Phone Number for the Order"
                  autoComplete="tel"
                  value={phoneForTheOrder}
                  onChangeText={(newText) => {
                    setPhoneError(undefined);
                    setPhoneForTheOrder(newText);
                  }}
                  placeholder="Enter a phone number for this order..."
                  errorMessage={phoneError}
                />
                <TextInput
                  className={styles.textInputEmail}
                  testId="email-for-order-input"
                  label="Email Address"
                  type="email"
                  autoComplete="email"
                  value={emailForTheOrder}
                  onChangeText={(newText) => {
                    setEmailError(undefined);
                    setEmailForTheOrder(newText);
                  }}
                  placeholder="Enter an email address for this order..."
                  errorMessage={emailError}
                />
                {promotionalCheckboxUIVariant === UIVariants.VARIANT_1 && (
                  <PromotionCheckbox
                    isConsentingToPromotions={isConsentingToPromotions}
                    setIsConsentingToPromotions={setIsConsentingToPromotions}
                    uiVariant={promotionalCheckboxUIVariant}
                  />
                )}
              </>
            )}
            <h3 className={styles.sectionTitle}>Payment Information</h3>
            <PaymentElement />
            {!customer &&
              promotionalCheckboxUIVariant === UIVariants.VARIANT_2 && (
                <PromotionCheckbox
                  isConsentingToPromotions={isConsentingToPromotions}
                  setIsConsentingToPromotions={setIsConsentingToPromotions}
                  uiVariant={promotionalCheckboxUIVariant}
                />
              )}
          </div>
          <CheckoutOrderDetails
            className={styles.orderDetails}
            areFieldsValid={areFieldsValid}
            nameForTheOrder={nameForTheOrder}
            phoneForTheOrder={phoneForTheOrder}
            emailForTheOrder={emailForTheOrder}
            isConsentingToPromotions={isConsentingToPromotions}
          />
        </div>
      </div>
    </Elements>
  );
};
